import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

const RequestPasswordResetPage = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('form'); // form, loading, success, error
  const [message, setMessage] = useState('');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setStatus('error');
      setMessage('Please enter your email address.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message);
      } else {
        setStatus('error');
        setMessage(data.detail || 'Failed to send reset email. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link 
            to="/login" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Link>
          
          <div className="bg-gray-800 rounded-lg p-8">
            {/* Status Icon */}
            <div className="mb-6">
              {status === 'success' ? (
                <div className="mx-auto h-16 w-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
              ) : status === 'error' ? (
                <div className="mx-auto h-16 w-16 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-red-400" />
                </div>
              ) : (
                <div className="mx-auto h-16 w-16 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <Mail className="h-8 w-8 text-purple-400" />
                </div>
              )}
            </div>

            <h1 className="text-2xl font-bold text-white mb-2">
              {status === 'success' ? 'Check Your Email' : 'Reset Your Password'}
            </h1>
            
            <p className="text-gray-300 mb-6">
              {status === 'success' 
                ? 'We\'ve sent you a password reset link if your email is verified and exists in our system.'
                : 'Enter your email address and we\'ll send you a link to reset your password.'
              }
            </p>

            {/* Success State */}
            {status === 'success' && (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
                  <p className="text-green-300 text-sm">
                    {message}
                  </p>
                </div>
                
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h3 className="font-semibold text-white mb-2">What's next?</h3>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>• Check your email inbox (and spam folder)</li>
                    <li>• Click the reset link within 1 hour</li>
                    <li>• Create your new password</li>
                  </ul>
                </div>

                <Link
                  to="/login"
                  className="block w-full text-center bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-colors"
                >
                  Back to Login
                </Link>
              </div>
            )}

            {/* Form State */}
            {status !== 'success' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Error Message */}
                {status === 'error' && message && (
                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                    <p className="text-red-300 text-sm">{message}</p>
                  </div>
                )}

                {/* Email Input */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                      required
                      disabled={status === 'loading'}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors ${
                    status === 'loading'
                      ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800'
                  }`}
                >
                  {status === 'loading' ? 'Sending Reset Link...' : 'Send Reset Link'}
                </button>
              </form>
            )}

            {/* Help Text */}
            <div className="mt-6 pt-6 border-t border-gray-700">
              <p className="text-center text-sm text-gray-400">
                Remember your password?{' '}
                <Link to="/login" className="text-purple-400 hover:text-purple-300 font-medium">
                  Sign in instead
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestPasswordResetPage;